/**
 * @file 导航菜单 RBAC 规则
 * @description 根据组员职阶与岗位判断各导航入口是否可见。
 * @remarks 规则对齐后端 MemberInternEnum；post 含 4 审稿即视为管理权限。
 */
import type { Member } from '@/api/generated/models'
import { getMemberPostIds } from '@/constants/post'

/** 工作职阶：正式组员(0) 或 实习组员(1) */
const WORKING_INTERN_STATUS: readonly number[] = [0, 1]

/** 生产岗位 ID：翻译/校对/嵌字/审稿/时轴/发布（排除「其他」5 与「图源」0） */
const PRODUCTION_POST_IDS: readonly number[] = [1, 2, 3, 4, 6, 7]

/** 导航入口标识 */
export type NavItemKey =
  /** 提灯甄选。 */
  | 'selection'
  /** 提灯问答（关于我们 / 社交媒体）。 */
  | 'qa'
  /** 我的资源（首页）。 */
  | 'resources'
  /** 提灯喵的组员们。 */
  | 'memberList'
  /** 我的书架。 */
  | 'bookshelf'
  /** 所有稿件。 */
  | 'uploaded'
  /** 组内活动。 */
  | 'groupActivity'
  /** 工单协作看板。 */
  | 'tickets'
  /** 发布漫画。 */
  | 'publish'
  /** 稿件监控。 */
  | 'monitor'
  /** 漫画与单话操作日志。 */
  | 'operationLog'
  /** 灯灯bot 管理查询。 */
  | 'dengdengAgent'
  /** 灰度发布。 */
  | 'grayRelease'
  /** 岗位目录。 */
  | 'postCatalog'
  /** 个人中心。 */
  | 'profile'
  /** 今日概览：上传稿件。 */
  | 'uploadAction'
  /** 今日概览：新建漫画。 */
  | 'createMangaAction'

/** 导航权限上下文 */
export interface NavPermissionContext {
  /** 是否已登录 */
  loggedIn: boolean
  /** 是否管理员（post 含 4 审稿） */
  isAdmin: boolean
  /** 组员职阶 */
  intern?: number
  /** 组员岗位 ID 列表 */
  postIds: number[]
}

/** 是否工作职阶（正式 / 实习） */
export const isWorkingMember = (intern?: number): boolean =>
  intern !== undefined && WORKING_INTERN_STATUS.includes(intern)

/** 是否拥有任一生产岗位 */
export const hasProductionPost = (postIds: number[]): boolean =>
  postIds.some((id) => PRODUCTION_POST_IDS.includes(id))

/**
 * 由登录组员信息构建权限上下文。
 * @param member 当前登录组员（未登录为空对象）
 * @returns 导航权限判断所需的最小上下文。
 */
export const buildNavPermissionContext = (
  member: Member | undefined | null
): NavPermissionContext => {
  const postIds = getMemberPostIds(member)
  return {
    loggedIn: Boolean(member?.username),
    isAdmin: postIds.includes(4),
    intern: member?.intern,
    postIds
  }
}

/**
 * 使用导航菜单的同一套 RBAC 规则校验当前组员权限。
 * @param key 待校验的权限入口。
 * @param member 当前登录组员。
 * @returns 当前组员可以访问对应入口时返回 true。
 */
export const canMemberAccessNavItem = (
  key: NavItemKey,
  member: Member | undefined | null
): boolean => canSeeNavItem(key, buildNavPermissionContext(member))

/**
 * 判断指定导航入口是否对当前上下文可见。
 * @param key 导航入口标识
 * @param ctx 权限上下文
 * @returns 当前上下文是否可以看到该导航入口。
 */
export const canSeeNavItem = (
  key: NavItemKey,
  ctx: NavPermissionContext
): boolean => {
  switch (key) {
    case 'qa':
      // 始终可见（含未登录访客）
      return true
    case 'resources':
    case 'profile':
      return ctx.loggedIn
    case 'memberList':
    case 'bookshelf':
    case 'groupActivity':
      // 已登录且为工作职阶
      return ctx.loggedIn && isWorkingMember(ctx.intern)
    case 'tickets':
      // 管理员可介入工单；其他入口仍仅对工作职阶开放。
      return ctx.loggedIn && (ctx.isAdmin || isWorkingMember(ctx.intern))
    case 'uploaded':
    case 'uploadAction':
      // 工作职阶且拥有生产岗位
      return (
        ctx.loggedIn &&
        isWorkingMember(ctx.intern) &&
        hasProductionPost(ctx.postIds)
      )
    case 'selection':
      return ctx.loggedIn
    case 'publish':
    case 'monitor':
    case 'operationLog':
    case 'dengdengAgent':
    case 'grayRelease':
    case 'postCatalog':
    case 'createMangaAction':
      // 仅管理员（审稿岗位）
      return ctx.loggedIn && ctx.isAdmin
    default:
      return false
  }
}
